import { useState } from "react";
import type { DashboardData, ExpenseInput, Owner } from "../../types";
import type { MooAction } from "../../types/moo";
import type { ReceiptDraft, ReceiptSplitPlan } from "../../types/receipt";
import { money, shortName, transferSummary } from "../../lib/finance";
import ExpenseForm from "../entry/ExpenseForm";
import TrackedItemsEditor from "../../components/TrackedItemsEditor";

// What the assistant is proposing to do to a transaction, shown before anything is written. The
// point is that nothing it suggests reaches the ledger until this has been read and accepted.

export default function ExpensePreview({
  action,
  receipt,
  data,
  profile,
  disabled,
  onRevise,
  onEditing,
}: {
  action: MooAction;
  receipt?: ReceiptDraft;
  data: DashboardData;
  profile: Owner;
  disabled: boolean;
  onRevise: (e: ExpenseInput, receiptSplit?: ReceiptSplitPlan, repairLinePrices?: boolean) => Promise<void>;
  onEditing: (v: boolean) => void;
}) {
  const [editing, setEditing] = useState(false),
    [showForm, setShowForm] = useState(false),
    [draft, setDraft] = useState(action.expense!),
    [formKey, setFormKey] = useState(0),
    [error, setError] = useState("");
  const [draftReceipt, setDraftReceipt] = useState(receipt);
  const expense = action.expense!,
    account = (data.accounts || []).find((a) => a.id === expense.accountId),
    toAccount = (data.accounts || []).find((a) => a.id === expense.toAccountId);
  const isTransfer = expense.type === "Transfer" || !!expense.toAccountId;
  const selected = new Set((draft.trackedItems || []).map((i) => i.id));
  const select = (ids: string[]) => {
    const items = receipt!.items
      .filter((i) => ids.includes(i.id))
      .map(
        (i) =>
          draft.trackedItems?.find((v) => v.id === i.id) || { ...i, notes: "" },
      );
    setDraft((d) => ({ ...d, trackedItems: items }));
    setFormKey((k) => k + 1);
    setEditing(true);
    onEditing(true);
  };
  return (
    <section className="moo-expense">
      <div className="moo-expense-heading">
        <strong>{isTransfer ? (expense.title || "Account transfer") : expense.title || "Set title"}</strong>
        <strong>
          {money(Number(expense.amount || 0), account?.currency || "MVR")}
        </strong>
      </div>
      <p>
        {expense.date || "Set date"} · {
          isTransfer
            ? `${account ? shortName(account.name) : "Account"} → ${toAccount ? shortName(toAccount.name) : "Account"}`
            : `${account ? shortName(account.name) : "Choose account"} · ${expense.allocations ? "Split across categories" : [expense.category || "Uncategorized", expense.subcategory].filter(Boolean).join(" · ")}`
        }
        {expense.tripId && (
          <> · ✈️ {(data.trips || []).find((t) => t.id === expense.tripId)?.name || "Trip"}{expense.tripDestination ? ` (${expense.tripDestination})` : ""}</>
        )}
      </p>
      {expense.allocations && (
        <ul aria-label="Category breakdown">
          {expense.allocations.map((part, index) => (
            <li key={index}>
              {part.category}{part.subcategory ? ` · ${part.subcategory}` : ""} · {money(Number(part.amount), account?.currency || "MVR")}
              {part.reportableAmount !== undefined && part.reportableAmount !== part.amount ? ` (household ${money(Number(part.reportableAmount), account?.currency || "MVR")})` : ""}
            </li>
          ))}
        </ul>
      )}
      {expense.payeeName && (
        <p>
          Payee: {expense.payeeName}
          {!(data.payees || []).some((p) => p.name.toLowerCase() === expense.payeeName!.toLowerCase()) ? " (new)" : ""}
        </p>
      )}
      {expense.reportableAmount !== undefined &&
        Number(expense.reportableAmount) !== Number(expense.amount) && (
          <p>
            Household {expense.type === "Income" ? "income" : "spending"}:{" "}
            {money(Number(expense.reportableAmount), account?.currency || "MVR")}
          </p>
        )}
      {expense.type === "Transfer" && <p>{transferSummary(expense, data.accounts)}</p>}
      {expense.notes && (
        <details>
          <summary>Notes</summary>
          <p>{expense.notes}</p>
        </details>
      )}
      {!!expense.trackedItems?.length && (
        <ul>
          {expense.trackedItems.map((i) => (
            <li key={i.id}>
              {i.name} · {i.total} {account?.currency}{" "}
              {i.measure && i.unit ? `for ${i.measure} ${i.unit}` : ""}
            </li>
          ))}
        </ul>
      )}
      {receipt && !disabled && (
        <details className="moo-receipt" open>
          <summary>
            {receipt.items.length} receipt items · {selected.size} tracked
          </summary>
          {receipt.warning && (
            <details>
              <summary>Scan notes</summary>
              <p>{receipt.warning}</p>
            </details>
          )}
          <div className="moo-select-actions">
            <button
              type="button"
              onClick={() =>
                select(
                  receipt.items
                    .filter((i) => i.total.trim() !== "" && Number.isFinite(Number(i.total)) && Number(i.total) >= 0)
                    .map((i) => i.id),
                )
              }
            >
              Select all
            </button>
            <button type="button" onClick={() => select([])}>
              Select none
            </button>
          </div>
          {receipt.items.map((i) => (
            <label
              key={i.id}
              className={`moo-receipt-item ${selected.has(i.id) ? "selected" : ""}`}
            >
              <input
                type="checkbox"
                checked={selected.has(i.id)}
                onChange={() =>
                  select(
                    selected.has(i.id)
                      ? [...selected].filter((id) => id !== i.id)
                      : [...selected, i.id],
                  )
                }
              />
              <span>
                {i.name || i.original}
                <small>
                  {i.quantity || "?"} bought {i.pack ? "· " + i.pack : ""}
                </small>
              </span>
              <strong>{i.total || "?"}</strong>
            </label>
          ))}
        </details>
      )}
      {!disabled && !showForm && (
        <button
          type="button"
          className="moo-edit"
          onClick={() => {
            if (!editing) { setDraft(expense); setDraftReceipt(receipt); }
            setError("");
            setEditing(true);
            setShowForm(true);
            onEditing(true);
          }}
        >
          Edit entry details
        </button>
      )}
      {editing && !showForm && (
        <div className="moo-edit-form">
          <TrackedItemsEditor
            items={draft.trackedItems || []}
            onChange={(items) =>
              setDraft((d) => ({ ...d, trackedItems: items }))
            }
            currency={account?.currency || "MVR"}
            disabled={disabled}
          />
          <button
            type="button"
            className="primary-button"
            disabled={disabled}
            onClick={async () => {
              try {
                setError("");
                await onRevise(draft, draftReceipt?.categorySplit, draftReceipt?.items.some((item, index) => item.total !== receipt?.items[index]?.total));
                setEditing(false);
                onEditing(false);
              } catch (e) {
                setError((e as Error).message);
              }
            }}
          >
            Update preview
          </button>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
      {editing && showForm && (
        <div className="moo-edit-form">
          <ExpenseForm
            key={formKey}
            data={data}
            profile={profile}
            initialInput={draft}
            receipt={draftReceipt}
            onReceiptChange={setDraftReceipt}
            autoFocusAmount={false}
            onSaved={() => {}}
            externalBusy={disabled}
            onDraftChange={setDraft}
            reviewLabel="Update preview"
            onReview={async (e) => {
              try {
                setError("");
                await onRevise(e, draftReceipt?.categorySplit, draftReceipt?.items.some((item, index) => item.total !== receipt?.items[index]?.total));
                setEditing(false);
                setShowForm(false);
                onEditing(false);
              } catch (e) {
                setError((e as Error).message);
              }
            }}
          />
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
